"use client";

import React, { useRef, useState, useEffect } from "react";
import Link from "next/link";
import Lenis from "lenis";
import Image from "next/image";

// --- PROJECT DATA ---
// --- PROJECT DATA ---
interface Project {
  id: string;
  title: string;
  code: string;
  isTextOnly?: boolean;
  link?: string;
  altDescription?: string; // Added for SEO
  images?: {
    desktop1: string; // Desktop Hero
    mobile: string;   // Mobile Home
    mobileDisplay: string; // Specific image for mobile view
  };
}

const projects: Project[] = [
  {
    id: "01",
    title: "Portfolio for Cinematographer",
    code: "hl",
    link: "/blog/portfolio-that-updates-itself",
    altDescription: "Automated Video Portfolio Infrastructure using Google Drive API and Mux video streaming",
    images: {
      desktop1: "/websites/hl1.avif",
      mobile: "/websites/hl.avif",
      mobileDisplay: "/websites/hl1.avif",
    },
  },
  {
    id: "02",
    title: "Brochure Website for Gym",
    code: "pf",
    link: "/blog/two-days-from-call-to-launch",
    altDescription: "Fast-loading Static Site for Planet Fitness Gym Franchise using Next.js and Tailwind CSS",
    images: {
      desktop1: "/websites/pf1.avif",
      mobile: "/websites/pf.avif",
      mobileDisplay: "/websites/pf1.avif",
    },
  },
  {
    id: "03",
    title: "Portfolio for Interior Designer",
    code: "is",
    link: "/blog/design-that-reassures",
    altDescription: "Minimalist Interior Design Portfolio with smooth animations and static site generation",
    images: {
      desktop1: "/websites/is1.avif",
      mobile: "/websites/is.avif",
      mobileDisplay: "/websites/is1.avif",
    },
  },
  {
    id: "04",
    title: "LMS Platform for Finance Tutor",
    code: "mwc",
    link: "/blog/lms-with-zero-bills",
    altDescription: "Zero-cost Learning Management System (LMS) using YouTube, Cashfree and Static Rendering",
    images: {
      desktop1: "/websites/mwc1.avif",
      mobile: "/websites/mwc.avif",
      mobileDisplay: "/websites/mwc1.avif",
    },
  },  {
    id: "05",
    title: "Here's what our clients have to say!",
    code: "clients",
    isTextOnly: true,
  },
];

// --- WINDOW CARD COMPONENT ---
interface WindowCardProps {
  project: Project;
  index: number;
  className?: string;
  style?: React.CSSProperties;
}

const WindowCard: React.FC<WindowCardProps> = ({ project, index, className = "", style }) => {
  const isTextOnly = project.isTextOnly;
  const isEven = index % 2 !== 0; // 0-indexed, so 1 and 3 are "even" in the user's alternating logic

  return (
    <div
      className={`scroll-stack-card relative w-full max-w-[1100px] mx-auto bg-[#e4e2dd] border-[3px] border-[#1a1a1a] shadow-[8px_8px_0px_#1a1a1a] flex flex-col overflow-hidden ${className}`.trim()}
      style={{
        ...style,
        backfaceVisibility: "hidden",
        transformStyle: "preserve-3d",
      }}
    >
      {/* Window Header */}
      <div className="bg-[#1a1a1a] text-[#e4e2dd] p-2 px-4 flex justify-between items-center font-mono select-none border-b-[3px] border-[#1a1a1a]">
        <span className="uppercase">
          {isTextOnly ? "CLIENTS.EXE" : `${project.code.toUpperCase()}.exe`}
        </span>
        <div className="flex gap-2">
          <div className="w-4 h-4 bg-[#e4e2dd] text-[#1a1a1a] flex items-center justify-center cursor-pointer hover:bg-white text-xs font-bold">
            -
          </div>
          <div className="w-4 h-4 bg-[#e4e2dd] text-[#1a1a1a] flex items-center justify-center cursor-pointer hover:bg-white text-xs font-bold">
            x
          </div>
        </div>
      </div>

      {/* Window Content */}
      <div className={`flex-1 bg-[#ccc] overflow-hidden relative flex flex-col ${isTextOnly ? "p-8 items-center justify-center text-center" : "p-4"}`}>
        {isTextOnly ? (
          <h2 className="pixel-font text-3xl md:text-5xl leading-tight">{project.title}</h2>
        ) : (
          <>
            {/* Mobile View: Single Image + Button */}
            <div className="md:hidden w-full flex-1 border-[2px] border-[#1a1a1a] bg-[#e4e2dd] overflow-hidden flex flex-col">
                <div className="flex-1 overflow-hidden relative aspect-[4/3]">
                    <Image 
                        src={project.images?.mobileDisplay || ""} 
                        alt={project.altDescription || project.title} 
                        fill
                        className="object-cover object-top"
                    />
                </div>
                {project.link && (
                    <div className="p-4 flex justify-center border-t-[2px] border-[#1a1a1a]">
                        <Link href={project.link} className="btn text-sm py-2 px-4">
                            View Case Study
                        </Link>
                    </div>
                )}
            </div>

            {/* Desktop View: Grid Layout */}
            <div className="hidden md:flex w-full h-full gap-4 items-stretch">
                {/* Layout A: Wide (Left) | Mobile (Right) */}
                {!isEven && (
                    <>
                        <div className="w-2/3 flex flex-col gap-4">
                            {/* Desktop Image - 2:1 Aspect Ratio */}
                            <div className="aspect-[2/1] border-[2px] border-[#1a1a1a] bg-[#e4e2dd] overflow-hidden relative">
                                <Image src={project.images?.desktop1 || ""} alt={project.altDescription || `${project.title} - Desktop Interface`} fill className="object-cover object-top" />
                            </div>
                            {/* Title Area - Fills remaining space */}
                            <div className="flex-1 border-[2px] border-[#1a1a1a] bg-[#e4e2dd] flex flex-col gap-4 items-center justify-center p-4">
                                <h2 className="pixel-font text-3xl md:text-4xl text-center leading-tight">{project.title}</h2>
                                {project.link && (
                                    <Link href={project.link} className="btn">
                                        View Case Study
                                    </Link>
                                )}
                            </div>
                        </div>
                        {/* Mobile Image - 9:16 Aspect Ratio */}
                        <div className="w-1/3 aspect-[9/16] border-[2px] border-[#1a1a1a] bg-[#e4e2dd] overflow-hidden relative">
                             <Image src={project.images?.mobile || ""} alt={`${project.altDescription || project.title} - Mobile View`} fill className="object-cover object-top" />
                        </div>
                    </>
                )}

                {/* Layout B: Mobile (Left) | Wide (Right) */}
                {isEven && (
                    <>
                        {/* Mobile Image - 9:16 Aspect Ratio */}
                        <div className="w-1/3 aspect-[9/16] border-[2px] border-[#1a1a1a] bg-[#e4e2dd] overflow-hidden relative">
                            <Image src={project.images?.mobile || ""} alt={`${project.altDescription || project.title} - Mobile View`} fill className="object-cover object-top" />
                        </div>
                        <div className="w-2/3 flex flex-col gap-4">
                            {/* Desktop Image - 2:1 Aspect Ratio */}
                            <div className="aspect-[2/1] border-[2px] border-[#1a1a1a] bg-[#e4e2dd] overflow-hidden relative">
                                <Image src={project.images?.desktop1 || ""} alt={project.altDescription || `${project.title} - Desktop Interface`} fill className="object-cover object-top" />
                            </div>
                            {/* Title Area - Fills remaining space */}
                            <div className="flex-1 border-[2px] border-[#1a1a1a] bg-[#e4e2dd] flex flex-col gap-4 items-center justify-center p-4">
                                <h2 className="pixel-font text-3xl md:text-4xl text-center leading-tight">{project.title}</h2>
                                {project.link && (
                                    <Link href={project.link} className="btn">
                                        View Case Study
                                    </Link>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// --- SCROLL STACK COMPONENT ---
interface ScrollStackProps {
  className?: string;
  useWindowScroll?: boolean;
}

const ScrollStack: React.FC<ScrollStackProps> = ({
  className = "",
  useWindowScroll = true,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const lenisRef = useRef<Lenis | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Setup Lenis (Smooth Scroll)
  useEffect(() => {
    if (isMobile) return;

    if (useWindowScroll) {
      const lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 2,
      });

      const raf = (time: number) => {
        lenis.raf(time);
        animationFrameRef.current = requestAnimationFrame(raf);
      };
      animationFrameRef.current = requestAnimationFrame(raf);

      lenisRef.current = lenis;

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        lenis.destroy();
      };
    }
  }, [useWindowScroll, isMobile]);

  // Mobile View: Static Grid
  if (isMobile) {
    return (
      <div className={`w-full py-12 px-4 ${className}`}>
        <div className="section-label mb-8 bg-white border-[2px] border-[#1a1a1a] px-2 py-1 inline-block text-sm">
          PROJECTS
        </div>
        <div className="flex flex-col gap-8">
          {projects.map((project, index) => (
            <WindowCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    );
  }

  // Desktop View: Sticky Stack
  return (
    <div className={`relative w-full ${className} border-b-[3px] border-[#1a1a1a]`}>
      <div className="scroll-stack-inner relative w-full p-8 pb-20" >
         {/* Sticky Header */}
         <div className="sticky top-8 left-8 z-50 mb-12 pointer-events-none">
            <div className="section-label bg-white border-[2px] border-[#1a1a1a] px-2 py-1 inline-block text-sm pointer-events-auto">
              PROJECTS
            </div>
         </div>

        <div className="max-w-[1200px] mx-auto px-4">
          {projects.map((project, i) => (
            <div 
              key={project.id} 
              className="sticky"
              style={{
                // Fixed top position for complete overlap. 
                // We add a tiny offset (i * 2px) just to prevent z-fighting rendering glitches, 
                // but effectively they will stack on top of each other.
                // The user wants "completely covering the previous one", so we don't want large offsets.
                top: `calc(15vh + ${i * 0}px)`, 
                
                // Margin bottom defines the scroll distance.
                // We want the last one to cover the 4th one completely.
                // If we give them all the same margin, they will stack evenly.
                marginBottom: i === projects.length - 1 ? "0vh" : "30vh",
                
                // Ensure correct z-index so they stack on top of each other
                zIndex: i + 1
              }}
            >
              <WindowCard project={project} index={i} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScrollStack;

